import React from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

const GlobalPagination = ({ 
  offset, 
  limit, 
  total, 
  onPageChange, 
  onLimitChange,
  className = "",
  showPageNumbers = true,
  maxVisiblePages = 5,
  showItemsPerPage = false
}) => {
  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;
  
  // Calculate the range being displayed
  const startItem = offset + 1;
  const endItem = Math.min(offset + limit, total);

  // Generate page numbers to display
  const getVisiblePages = () => {
    if (totalPages <= maxVisiblePages) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    // Adjust if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  };

  const handlePageChange = (newOffset) => {
    if (newOffset >= 0 && newOffset < total) {
      onPageChange(newOffset);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      handlePageChange(offset - limit);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      handlePageChange(offset + limit);
    }
  };

  const handlePageClick = (pageNumber) => {
    const newOffset = (pageNumber - 1) * limit;
    handlePageChange(newOffset);
  };

  const handleLimitChange = (e) => {
    const newLimit = parseInt(e.target.value);
    if (onLimitChange) {
      // Reset to first page when limit changes
      onLimitChange(newLimit, 0);
    }
  };

  // Don't render if no items
  if (total <= 0) {
    return null;
  }

  const visiblePages = getVisiblePages();

  return (
    <div className={`flex flex-col sm:flex-row justify-between items-center gap-4 p-4 bg-white shadow-sm rounded-lg border ${className}`}>
      {/* Range Information - Show when items per page selector is disabled */}
      {!showItemsPerPage && (
        <div className="text-sm text-gray-600">
          Showing {startItem} to {endItem} of {total} results
        </div>
      )}
      
      {/* Items per page selector - Only show if enabled */}
      {showItemsPerPage && (
        <div className="flex items-center gap-2">
          <label htmlFor="itemsPerPage" className="text-sm text-gray-600">
            Items per page:
          </label>
          <select
            id="itemsPerPage"
            value={limit}
            onChange={handleLimitChange}
            className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {/* <option value={10}>10</option> */}
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      )}

      {/* Pagination Controls - Only show if more than 1 page */}
      {totalPages > 1 && (
      <div className="flex items-center space-x-2">
        {/* Previous Button */}
        <button
          onClick={handlePrevious}
          disabled={currentPage === 1}
          className={`p-2 rounded-md transition-colors ${
            currentPage === 1
              ? "text-gray-400 cursor-not-allowed"
              : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
          }`}
          aria-label="Previous page"
        >
          <ChevronLeftIcon className="w-5 h-5" />
        </button>

        {/* Page Numbers */}
        {showPageNumbers && (
          <div className="flex items-center space-x-1">
            {visiblePages.map((page) => (
              <button
                key={page}
                onClick={() => handlePageClick(page)}
                className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  page === currentPage
                    ? "bg-blue-600 text-white"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                }`}
              >
                {page}
              </button>
            ))}
          </div>
        )}

        {/* Next Button */}
        <button
          onClick={handleNext}
          disabled={currentPage === totalPages}
          className={`p-2 rounded-md transition-colors ${
            currentPage === totalPages
              ? "text-gray-400 cursor-not-allowed"
              : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
          }`}
          aria-label="Next page"
        >
          <ChevronRightIcon className="w-5 h-5" />
        </button>
      </div>
      )}

      {/* Page Info (for mobile) */}
      {totalPages > 1 && (
      <div className="text-sm text-gray-600 sm:hidden">
        Page {currentPage} of {totalPages}
      </div>
      )}
    </div>
  );
};

export default GlobalPagination; 